import React, { useCallback, useEffect, useState } from 'react';
import {
	CheckMarkGreen,
	CheckboxChecked,
	CheckboxUnchecked,
	ChevronColorless,
	TrashOutline
} from '@/resources/assets/images';
import {
	addDays,
	endOfDay,
	endOfMonth,
	endOfWeek,
	isSameDay,
	startOfMonth,
	startOfWeek,
	addMonths
} from 'date-fns';
import DatePicker from '@/components/DatePicker/DatePicker';
import { createStaticRanges, defaultStaticRanges } from 'react-date-range';
import './ScheduledEmailsFilter.scss';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import {
	emailCancelScheduledBatchEmailsClearStatus,
	emailCancelScheduledBatchEmailsUser,
	emailGetScheduledEmails,
	emailGetScheduledEmailsCountByDate
} from '@/modules/email/emailActions';
import Loader, { Status } from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import Button from '@/components/Button/newButtons';
import type { Email } from '@/modules/email/emailTypes';

type ScheduledEmailsFilterProps = {
	selectedEmails: string[];
	setSelectedEmails: React.Dispatch<React.SetStateAction<string[]>>;
};

const ScheduledEmailsFilter = ({
	selectedEmails,
	setSelectedEmails
}: ScheduledEmailsFilterProps): JSX.Element => {
	const [
		user,
		scheduledEmails,
		scheduledEmailsCountByDate,
		batchCancelScheduledEmailsLoading,
		batchCancelScheduledEmailsSuccess,
		batchCancelScheduledEmailsFailure
	] = useSelector(
		(state: RootState) => [
			state.user.getIn(['authorize', 'user']).toJS(),
			state.email.getIn(['email', 'scheduled', 'emails']),
			state.email.getIn(['email', 'scheduled', 'countByDate']),
			state.email.getIn(['email', 'scheduledbatch', 'cancel', 'loading']),
			state.email.getIn(['email', 'scheduledbatch', 'cancel', 'success']),
			state.email.getIn(['email', 'scheduledbatch', 'cancel', 'failure'])
		],
		shallowEqual
	);
	const [isDropdownVisible, setIsDropdownVisible] = useState(false);
	const [selectedDateRange, setSelectedDateRange] = useState<{
		startDate: Date;
		endDate: Date;
	}>({});
	const [query, setQuery] = useState<{ limit: number; skip: number }>({
		limit: 150,
		skip: 0
	});
	let startDateRef;
	let endDateRef;
	const WEEK_STARTS_MON = 1;
	const staticRangesAdmin = createStaticRanges([
		...(defaultStaticRanges.slice(0, 1) as Range),
		{
			label: 'This Week',
			range: () => ({
				startDate: startOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				}),
				endDate: endOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				})
			}),
			isSelected: (range: Range) => {
				isSameDay(
					range.startDate as Date,
					startOfWeek(new Date(), {
						weekStartsOn: WEEK_STARTS_MON
					})
				) &&
					isSameDay(
						range.endDate as Date,
						endOfWeek(new Date(), {
							weekStartsOn: WEEK_STARTS_MON
						})
					),
					(startDateRef = range.startDate);
				endDateRef = endOfDay(range.endDate);
			}
		},
		{
			label: 'Next two weeks',
			range: () => ({
				startDate: startOfWeek(addDays(new Date(), +7), {
					weekStartsOn: WEEK_STARTS_MON
				}),
				endDate: endOfWeek(addDays(new Date(), +14), {
					weekStartsOn: WEEK_STARTS_MON
				})
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfWeek(addDays(new Date(), +7), {
						weekStartsOn: WEEK_STARTS_MON
					})
				) &&
				isSameDay(
					range.endDate as Date,
					endOfWeek(addDays(new Date(), +14), {
						weekStartsOn: WEEK_STARTS_MON
					})
				)
		},
		{
			label: 'This Month',
			range: () => ({
				startDate: startOfMonth(addMonths(new Date(), 0)),
				endDate: endOfMonth(addMonths(new Date(), 0))
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfMonth(addMonths(new Date(), 0))
				) &&
				isSameDay(
					range.endDate as Date,
					endOfMonth(addMonths(new Date(), 0))
				)
		},
		{
			label: 'Next Two Months',
			range: () => ({
				startDate: startOfMonth(addMonths(new Date(), 0)),
				endDate: endOfMonth(addMonths(new Date(), 1))
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfMonth(addMonths(new Date(), 0))
				) &&
				isSameDay(
					range.endDate as Date,
					endOfMonth(addMonths(new Date(), 1))
				)
		}
	]);

	const dispatch = useDispatch();

	const getScheduledEmails = useCallback(
		payload => dispatch(emailGetScheduledEmails(payload)),
		[dispatch]
	);
	const getScheduledEmailsCountByDate = useCallback(
		payload => dispatch(emailGetScheduledEmailsCountByDate(payload)),
		[dispatch]
	);
	const cancelScheduledBatchEmailsUser = (businessIds: string[]) =>
		dispatch(
			emailCancelScheduledBatchEmailsUser({
				businessIdArray: businessIds
			})
		);
	const cancelScheduledBatchEmailsClearStatus = () =>
		dispatch(emailCancelScheduledBatchEmailsClearStatus());

	const setDateRange = useCallback(
		(startDate, endDate) => {
			setQuery({ limit: 150, skip: 0 });
			setSelectedDateRange({ startDate, endDate });
			getScheduledEmails({
				startDateDT: startDate,
				endDateDT: endDate,
				skip: 0,
				limit: 150
			});
			getScheduledEmailsCountByDate({ startDate, endDate });
		},
		[getScheduledEmails, getScheduledEmailsCountByDate]
	);

	useEffect(() => {
		const startDate = new Date();
		const endDate = new Date(startDate.getTime());
		endDate.setDate(startDate.getDate() + 14);
		setDateRange(startDate, endDate);
	}, [setDateRange]);

	const openScheduledSendCancelModal = () => {
		const e = document.getElementById('scheduled-send-batch-cancel-modal');
		e?.classList.remove('scheduled-send-batch-cancel-modal-fade');
		setTimeout(() => {
			e?.classList.remove('scheduled-send-batch-cancel-modal-closed');
		}, 500);
	};

	const closeScheduledSendCancelModal = () => {
		const e = document.getElementById('scheduled-send-batch-cancel-modal');
		e?.classList.add('scheduled-send-batch-cancel-modal-fade');
		setTimeout(() => {
			e?.classList.add('scheduled-send-batch-cancel-modal-closed');
		}, 500);

		cancelScheduledBatchEmailsClearStatus();
	};

	const cancelScheduledEmailButtonText = () => {
		if (batchCancelScheduledEmailsLoading) {
			return 'Cancelling';
		}

		if (batchCancelScheduledEmailsSuccess) {
			return 'Cancelled';
		}

		return 'Cancel scheduled';
	};
	const selectQuantity = (quantity: number) => {
		const toSelect = scheduledEmails
			.slice(0, quantity)
			.map((email: Email) => email.businessId);
		setSelectedEmails([...new Set([...selectedEmails, ...toSelect])]); // Avoid duplicates
		setIsDropdownVisible(false); // Hide dropdown after selection
	};

	useEffect(() => {
		if (batchCancelScheduledEmailsSuccess) {
			setTimeout(() => {
				closeScheduledSendCancelModal();
				getScheduledEmailsCountByDate(selectedDateRange);
			}, 1000);

			getScheduledEmails({
				startDateDT: selectedDateRange.startDate,
				endDateDT: selectedDateRange.endDate,
				skip: 0,
				limit: 150
			});

			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User cancelled scheduled emails',
				properties: {
					businessIds: selectedEmails,
					numberOfEmails: selectedEmails.length,
					startDate: selectedDateRange.startDate,
					endDate: selectedDateRange.endDate
				}
			});
			setSelectedEmails([]);
		}
	}, [batchCancelScheduledEmailsSuccess, getScheduledEmails]);

	const paginate = direction => {
		const newQuery = { ...query };

		if (direction === 'forward') {
			newQuery.skip += newQuery.limit;
		} else if (direction === 'backward') {
			newQuery.skip -= newQuery.limit;
		}

		setQuery(newQuery);

		getScheduledEmails({
			startDateDT: selectedDateRange.startDate,
			endDateDT: selectedDateRange.endDate,
			skip: newQuery.skip,
			limit: newQuery.limit
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `Paginated Scheduled Emails Page`,
			properties: {
				paginationDirection: direction
			}
		});
	};

	const datePickerReportUserPeriod = (
		selectionStart?: Date,
		selectionEnd?: Date
	) => {
		const startDate = new Date();
		const endDate = new Date(startDate.getTime());
		endDate.setDate(startDate.getDate() + 14);

		return {
			startDate: selectionStart || startDate,
			endDate: selectionEnd || endDate
		};
	};

	const cancelEmailsButtonClickHandler = () => {
		if (selectedEmails.length > 0) {
			cancelScheduledBatchEmailsUser(selectedEmails);
		}
	};

	const selectAllButtonClickHandler = () => {
		if (selectedEmails.length === scheduledEmailsCountByDate) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				properties: {
					unSelectedEmails: selectedEmails
				},
				eventName: 'User unselected all scheduled emails'
			});
			setSelectedEmails([]);
			return;
		}

		if (scheduledEmailsCountByDate > 150) {
			getScheduledEmails({
				startDateDT: selectedDateRange.startDate,
				endDateDT: selectedDateRange.endDate,
				skip: 0,
				limit: scheduledEmailsCountByDate // Fetch all emails
			});
			return;
		}

		const selectedCheckboxEmails = scheduledEmails.map(
			email => email.businessId
		);

		setSelectedEmails(selectedCheckboxEmails);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			properties: {
				selectedCheckboxEmails
			},
			eventName: 'User duplicated sequence step'
		});
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (!event.target.closest('.scheduled-emails-column-label')) {
				setIsDropdownVisible(false);
			}
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, []);

	useEffect(() => {
		// Ensure that this only runs when the full list of emails is fetched
		if (
			scheduledEmails.length === scheduledEmailsCountByDate &&
			scheduledEmailsCountByDate > 150
		) {
			// Select all emails
			setSelectedEmails(scheduledEmails.map(email => email.businessId));
		}
	}, [scheduledEmails, scheduledEmailsCountByDate, setSelectedEmails]);
	const removeAllChecks = () => {
		setSelectedEmails([]);

		const actives = document.getElementsByClassName('activated');
		const arr = [];
		for (let i = 0; i < actives.length; i++) {
			arr.push(actives[i]);
		}
		arr.forEach(el => {
			el.classList.remove('activated');
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User unselected all selected scheduled emails',
			properties: {
				selectedEmails
			}
		});
	};
	return (
		<div className="scheduled-emails-container">
			<div
				className="scheduled-send-batch-cancel-modal scheduled-send-batch-cancel-modal-closed scheduled-send-batch-cancel-modal-fade"
				id="scheduled-send-batch-cancel-modal"
			>
				<div className="scheduled-send-batch-cancel-modal-background" />
				<div className="scheduled-send-batch-cancel-inner-modal">
					<div className="scheduled-send-batch-cancel-modal-title">
						Are you sure you want to cancel the selected emails?
					</div>
					{batchCancelScheduledEmailsFailure && (
						<div className="scheduled-send-batch-cancel-failure">
							Something went wrong, please try againsdf.
						</div>
					)}
					<span className="scheduled-send-batch-cancel-button-holder">
						{!batchCancelScheduledEmailsSuccess && (
							<button
								type="button"
								className="scheduled-send-batch-cancel-secondary"
								onClick={closeScheduledSendCancelModal}
							>
								Close
							</button>
						)}
						<button
							type="button"
							id="scheduled-send-inner-cancel"
							className={`scheduled-send-batch-cancel-primary ${batchCancelScheduledEmailsSuccess ? 'success' : ''}`}
							disabled={batchCancelScheduledEmailsSuccess}
							onClick={cancelEmailsButtonClickHandler}
						>
							{cancelScheduledEmailButtonText()}
							{batchCancelScheduledEmailsLoading && (
								<Loader
									className="scheduled-send-batch-cancel-primary-loader"
									status={Status.LOADING}
								/>
							)}
							{batchCancelScheduledEmailsSuccess && (
								<CheckMarkGreen className="scheduled-send-batch-cancel-primary-checkmark" />
							)}
						</button>
					</span>
				</div>
			</div>
			<div className="flex justify-between">
				<div className="scheduled-emails-button-container-redesign">
					<Button
						onClick={selectAllButtonClickHandler}
						variant="primary"
					>
						{selectedEmails.length === scheduledEmailsCountByDate &&
						selectedEmails.length > 0
							? 'Unselect All Emails'
							: 'Select All Emails'}
					</Button>
					<div className="cancel-emails-button-wrapper">
						<Button
							disabled={selectedEmails.length === 0}
							variant="secondary"
							onClick={openScheduledSendCancelModal}
							red
							leftIcon={<TrashOutline />}
						>
							Cancel Emails
						</Button>
						{selectedEmails.length === 0 && (
							<div className="tooltip-scheduled-emails">
								Must have emails selected in order to cancel
								them.
							</div>
						)}
					</div>
				</div>
				<div className="scheduled-emails-date-container">
					<DatePicker
						wrapperClassName="communication-center-date-picker"
						minDate={new Date()}
						maxDate={undefined}
						reportUserPeriod={datePickerReportUserPeriod(
							selectedDateRange.startDate,
							selectedDateRange.endDate
						)}
						onChange={evt => {
							setDateRange(evt.startDate, evt.endDate);
							datePickerReportUserPeriod(
								selectedDateRange.startDate,
								selectedDateRange.endDate
							);
							sendAnalytics({
								type: 'track',
								userId: user.email,
								eventName: `Filtered scheduled emails by Date`,
								properties: {
									startDate: selectedDateRange.startDate,
									endDate: selectedDateRange.endDate
								}
							});
						}}
						staticRanges={staticRangesAdmin}
					/>
					<div className="scheduled-emails-pagination">
						<ChevronColorless
							className="scheduled-emails-pagination-arrow"
							onClick={() => {
								if (query.skip - query.limit < 0) {
									return;
								}

								paginate('backward');
							}}
						/>
						{scheduledEmailsCountByDate > 0 && (
							<span className="scheduled-emails-pagination-count-text">
								{query.skip} -{' '}
								{query.skip + query.limit >
								scheduledEmailsCountByDate
									? scheduledEmailsCountByDate
									: query.skip + query.limit}
								<span className="scheduled-emails-pagination-count-text-of">
									&nbsp;of&nbsp;
									{scheduledEmailsCountByDate}
								</span>
							</span>
						)}
						{scheduledEmailsCountByDate === 0 && (
							<span className="communication-center-count-text">
								0 - 0
								<span className="communication-center-count-text-of">
									&nbsp;of&nbsp; 0
								</span>
							</span>
						)}
						<ChevronColorless
							className="scheduled-emails-pagination-arrow right"
							onClick={() => {
								if (
									query.skip + query.limit >=
									scheduledEmailsCountByDate
								) {
									return;
								}

								paginate('forward');
							}}
						/>
					</div>
				</div>
			</div>
			<div className="scheduled-emails-list-header">
				<div
					className={`communication-center-business-list-save-list ${
						Object.keys(selectedEmails).length > 0
							? 'save-list-open'
							: ''
					}`}
				>
					<div className="list-item-checkbox-outer list-item-checkbox-hidden-nav">
						<div
							className="list-item-checkbox-inner list-item-checkbox-inner-hidden-nav"
							onClick={removeAllChecks}
						>
							<div className="checkbox-white-bar" />
						</div>
					</div>
					<div className="communication-center-business-list-save-count">
						{Object.keys(selectedEmails).length} selected
					</div>
				</div>
				<div className="scheduled-emails-column-labels-container-redesign">
					<div className="scheduled-emails-column-label">
						<button
							type="button"
							className="scheduled-emails-list-item-checkbox"
							onClick={() => {
								setIsDropdownVisible(!isDropdownVisible);
							}}
						>
							{scheduledEmails.every(email =>
								selectedEmails.includes(email.businessId)
							) && selectedEmails.length > 0 ? (
								<CheckboxChecked />
							) : (
								<CheckboxUnchecked />
							)}
						</button>
					</div>
					<div className="scheduled-emails-column-label">Name</div>
					<div className="scheduled-emails-column-label">Email</div>
					<div className="scheduled-emails-column-label">Content</div>
					<div className="scheduled-emails-column-label-scheduled-date">
						Scheduled Date
					</div>
				</div>
			</div>
			{/* Dropdown rendered outside header */}
			{isDropdownVisible && (
				<div className="dropdown-container">
					<div className="select-number-outer-redesign">
						<div
							className="select-number-item"
							onClick={() => selectQuantity(50)}
						>
							Select next 50
						</div>
						<div
							className="select-number-item"
							onClick={() => selectQuantity(100)}
						>
							Select next 100
						</div>
						<div
							className="select-number-item"
							onClick={() => selectQuantity(150)}
						>
							Select next 150
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ScheduledEmailsFilter;
